// components/sequences/SequenceForm.tsx
import React, { useEffect, useState } from 'react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Input } from '../ui/Input';
import { InvoiceNumberSequence } from '@/types/invoiceNumberSeq.types';

interface SequenceFormProps {
  sequence?: InvoiceNumberSequence;
  onSave: (sequence: Omit<InvoiceNumberSequence, 'sequence_id' | 'last_reset_date'>) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

export function SequenceForm({
  sequence,
  onSave,
  onCancel,
  isSubmitting
}: SequenceFormProps) {
  const [formData, setFormData] = useState<Omit<InvoiceNumberSequence, 'sequence_id' | 'last_reset_date'>>({
    sequence_name: '',
    prefix: '',
    suffix: '',
    next_number: 1,
    number_padding: 4,
    is_default: false,
    reset_frequency: 'yearly'
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  useEffect(() => {
    if (sequence) {
      setFormData({
        sequence_name: sequence.sequence_name,
        prefix: sequence.prefix,
        suffix: sequence.suffix,
        next_number: sequence.next_number,
        number_padding: sequence.number_padding,
        is_default: sequence.is_default,
        reset_frequency: sequence.reset_frequency
      });
    }
  }, [sequence]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked 
        : type === 'number' ? Number(value) 
        : value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.sequence_name.trim()) {
      newErrors.sequence_name = 'Sequence name is required';
    }
    
    if (formData.next_number < 1) {
      newErrors.next_number = 'Next number must be at least 1';
    }
    
    if (formData.number_padding < 1 || formData.number_padding > 10) {
      newErrors.number_padding = 'Number padding must be between 1 and 10';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave(formData);
    }
  };
  
  // Create a preview of what the formatted number will look like
  const previewNumber = String(formData.next_number).padStart(formData.number_padding, '0');
  const previewFormat = `${formData.prefix}${previewNumber}${formData.suffix}`;
  
  return (
    <Card title={sequence ? 'Edit Sequence' : 'Create New Sequence'}>
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Input
            label="Sequence Name"
            name="sequence_name"
            value={formData.sequence_name}
            onChange={handleChange}
            error={errors.sequence_name}
            required
          />
          
          <div className="md:col-span-2">
            <label className="text-sm font-medium text-gray-700 block mb-1">Preview</label>
            <div className="p-3 bg-gray-100 rounded-sm font-mono text-gray-800">
              {previewFormat}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Example of how the next invoice number will appear
            </p>
          </div>
          
          <Input
            label="Prefix"
            name="prefix"
            value={formData.prefix}
            onChange={handleChange}
            helpText="Text to appear before the number (e.g. 'INV-')"
          />
          
          <Input
            label="Suffix"
            name="suffix"
            value={formData.suffix}
            onChange={handleChange}
            helpText="Text to appear after the number (e.g. '-2025')"
          />
          
          <Input
            label="Next Number"
            name="next_number"
            type="number"
            value={formData.next_number.toString()}
            onChange={handleChange}
            error={errors.next_number}
            required
            min={1}
            helpText="The next number to be used in the sequence"
          />
          
          <Input
            label="Number Padding"
            name="number_padding"
            type="number"
            value={formData.number_padding.toString()}
            onChange={handleChange}
            error={errors.number_padding}
            helpText="Number of digits (e.g. 4 for '0001')"
            required
            min={1}
            max={10}
          />
          
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Reset Frequency</label>
            <select
              name="reset_frequency"
              value={formData.reset_frequency}
              onChange={handleChange}
              className="darkselect w-full h-10 px-4 border border-gray-300 rounded-sm focus:ring-2 focus:ring-primary-light focus:border-primary outline-none"
            >
              <option value="never">Never</option>
              <option value="yearly">Yearly</option>
              <option value="monthly">Monthly</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              When to reset the number back to the start
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              name="is_default"
              id="is_default"
              checked={formData.is_default}
              onChange={handleChange}
              className="h-4 w-4 rounded border-gray-300 text-gold focus:ring-gold"
            />
            <label htmlFor="is_default" className="text-sm font-medium text-gray-700">
              Set as Default Sequence
            </label>
          </div>
        </div>
        
        <div className="flex justify-end space-x-4">
          <Button className='actionBtn border-gold bg-transparent hover:bg-gold text-gold hover:text-white' type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting}>
            {sequence ? 'Update Sequence' : 'Create Sequence'}
          </Button>
        </div>
      </form>
    </Card>
  );
}